/**
 * The closed vocabularies a learning may carry about visuals (ported from 1.0
 * `lib/ad-image-prompt.ts` and `learning-engine-service.ts`).
 *
 * Security invariant: a style or tag read back from storage reaches a prompt only through
 * these allowlists and the fixed phrases below. A shape regex is not enough — a hostile
 * lowercase token is regex-valid — so membership is the test, and the only wording a tag can
 * contribute is its template phrase.
 */

export const IMAGE_STYLES = ["lifestyle", "product", "abstract", "social"] as const;
export const VIDEO_STYLES = ["ugc", "cinematic", "product", "motion"] as const;
export type ImageStyle = (typeof IMAGE_STYLES)[number];
export type VideoStyle = (typeof VIDEO_STYLES)[number];

const KNOWN_STYLES: ReadonlySet<string> = new Set<string>([...IMAGE_STYLES, ...VIDEO_STYLES]);

export const VISUAL_TAGS = [
  "warm_palette",
  "cool_palette",
  "vivid_palette",
  "neutral_palette",
  "indoor",
  "outdoor",
  "studio",
  "person",
  "no_person",
  "close_up",
  "wide_shot",
] as const;
export type VisualTag = (typeof VISUAL_TAGS)[number];
const TAG_SET: ReadonlySet<string> = new Set<string>(VISUAL_TAGS);

export const VISUAL_TAG_PHRASES: Readonly<Record<VisualTag, string>> = {
  warm_palette: "a warm color palette",
  cool_palette: "a cool color palette",
  vivid_palette: "a vivid saturated color palette",
  neutral_palette: "a restrained neutral color palette",
  indoor: "an indoor setting",
  outdoor: "an outdoor setting",
  studio: "a controlled studio setting",
  person: "people visibly present",
  no_person: "no people in frame",
  close_up: "close-up framing",
  wide_shot: "wide framing",
};

const IMAGE_STYLE_TAGS: Readonly<Record<ImageStyle, readonly VisualTag[]>> = {
  lifestyle: ["warm_palette", "indoor", "person"],
  product: ["neutral_palette", "studio", "no_person"],
  abstract: ["vivid_palette", "studio", "no_person"],
  social: ["vivid_palette", "outdoor", "person"],
};

const VIDEO_STYLE_TAGS: Readonly<Record<VideoStyle, readonly VisualTag[]>> = {
  ugc: ["warm_palette", "indoor", "person"],
  cinematic: ["cool_palette", "outdoor", "person"],
  product: ["neutral_palette", "studio", "no_person"],
  motion: ["vivid_palette", "studio", "no_person"],
};

export function isKnownStyle(value: unknown): value is ImageStyle | VideoStyle {
  return typeof value === "string" && KNOWN_STYLES.has(value);
}

export function isImageStyle(value: unknown): value is ImageStyle {
  return typeof value === "string" && (IMAGE_STYLES as readonly string[]).includes(value);
}

export function isVisualTag(value: unknown): value is VisualTag {
  return typeof value === "string" && TAG_SET.has(value);
}

/** Tags for a generated image, from the style and the true aspect ratio alone. */
export function deriveImageTags(style: string, aspectRatio: string): VisualTag[] {
  const tags: VisualTag[] = isImageStyle(style) ? [...IMAGE_STYLE_TAGS[style]] : [];
  tags.push(aspectRatio === "3:2" || aspectRatio === "16:9" ? "wide_shot" : "close_up");
  return tags;
}

export function deriveVideoTags(style: string, aspectRatio: string): VisualTag[] {
  const known = (VIDEO_STYLES as readonly string[]).includes(style) ? (style as VideoStyle) : null;
  const tags: VisualTag[] = known === null ? [] : [...VIDEO_STYLE_TAGS[known]];
  tags.push(aspectRatio === "16:9" || aspectRatio === "4:3" ? "wide_shot" : "close_up");
  return tags;
}

export interface BoundedVisual {
  readonly styles: (ImageStyle | VideoStyle)[];
  readonly tags: VisualTag[];
}

/** Keeps only allowlisted tokens; everything else — including hostile text — is dropped. */
export function boundVisual(input: {
  readonly styles?: unknown;
  readonly tags?: unknown;
}): BoundedVisual {
  const styles = Array.isArray(input.styles) ? input.styles.filter(isKnownStyle) : [];
  const tags = Array.isArray(input.tags) ? input.tags.filter(isVisualTag) : [];
  return { styles: [...new Set(styles)], tags: [...new Set(tags)] };
}

export interface VisualLearningVote {
  readonly outcome: "winner" | "worn";
  readonly visual: BoundedVisual;
}

export interface VisualGuidance {
  readonly preferredStyle: ImageStyle | VideoStyle | null;
  readonly winningTags: VisualTag[];
  readonly wornStyles: (ImageStyle | VideoStyle)[];
  readonly wornTags: VisualTag[];
  /** Assembled from fixed phrases only; safe to place in a prompt. */
  readonly promptBlock: string;
}

/** Winners outrank fatigue: a style that keeps winning is never reported as worn. */
export function visualGuidance(votes: readonly VisualLearningVote[]): VisualGuidance {
  const styleVotes = new Map<string, number>();
  const tagVotes = new Map<string, number>();
  const wornStyleVotes = new Map<string, number>();
  const wornTagVotes = new Map<string, number>();
  for (const vote of votes) {
    const bounded = boundVisual(vote.visual);
    const styles = vote.outcome === "winner" ? styleVotes : wornStyleVotes;
    const tags = vote.outcome === "winner" ? tagVotes : wornTagVotes;
    for (const style of bounded.styles) styles.set(style, (styles.get(style) ?? 0) + 1);
    for (const tag of bounded.tags) tags.set(tag, (tags.get(tag) ?? 0) + 1);
  }
  const top = (map: Map<string, number>, limit: number): string[] =>
    [...map.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .slice(0, limit)
      .map(([token]) => token);
  const preferred = top(styleVotes, 1)[0] ?? null;
  const winningTags = top(tagVotes, 4).filter(isVisualTag);
  for (const style of styleVotes.keys()) wornStyleVotes.delete(style);
  for (const tag of tagVotes.keys()) wornTagVotes.delete(tag);
  const wornStyles = top(wornStyleVotes, 3).filter(isKnownStyle);
  const wornTags = top(wornTagVotes, 3).filter(isVisualTag);
  const lines: string[] = [];
  if (winningTags.length > 0) {
    lines.push(
      `Visuals that have won for this brand favour ${winningTags.map((tag) => VISUAL_TAG_PHRASES[tag]).join(", ")}.`,
    );
  }
  if (wornTags.length > 0) {
    lines.push(
      `Avoid leaning on ${wornTags.map((tag) => VISUAL_TAG_PHRASES[tag]).join(", ")}; those looks have worn out.`,
    );
  }
  return {
    preferredStyle: preferred !== null && isKnownStyle(preferred) ? preferred : null,
    winningTags,
    wornStyles,
    wornTags,
    promptBlock: lines.join(" "),
  };
}
